import * as React from 'react';
import { useSelector } from 'react-redux';

import { getSubmissionDetailsLoading, getSovLocations } from 'application/redux/selectors';
import { CircularLoader } from 'components/Loader';
import LrGrid from 'components/LrGrid';
import { UwGridRow, UwReview, UwLocationReview, DedBuyBackData } from 'types/underwriting.type';

import { columnDefs, defaultColDef } from './GridDefs';
import { Page, Spacing } from '../../../Layout';
import { LabeledData } from '../../../Input';
import { formatDollar, formatPercent } from '../../../../helpers/formatters';
import { Typography } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import LrButton from '../../../LrButton';
import { useActions } from '../../../../hooks/useActions';
import * as Actions from '../../../../application/redux/actions';
import { SubmissionDetails } from '../../../../types/submission.type';
import { fetchDBBData } from '../../../../services/quotes';
import { useCallback } from 'react';
import DBBCreateQuoteDialog from './DBBCreateQuoteDialog';

interface Props {
    selectedUWReview: UwReview;
    isLoading: boolean;
}

const UwSummary: React.FunctionComponent<Props> = (props) => {
    const { isLoading, selectedUWReview } = props;

    // Global State
    const submissionIsLoading = useSelector(getSubmissionDetailsLoading);
    const locations = useSelector(getSovLocations);

    // Local State
    const [gridData, setGridData] = React.useState([] as UwGridRow[]);
    const [currentDbbItem, setCurrentDbbItem] = React.useState<DedBuyBackData>();
    const [dbbData, setDbbData] = React.useState<Record<string, DedBuyBackData>>({});

    const createGridData = () => {
        if (!locations.length || !selectedUWReview.locationReviews) return;

        const data = selectedUWReview.locationReviews.map(
            ({
                finalRate,
                baseRate,
                CR_DB,
                totalPremium,
                lumenRating,
                totalInsurableValue,
                locationName,
                locationZone,
                rowId,
            }: UwLocationReview) => {
                return {
                    locationId: String(rowId),
                    locationName: locationName || '--',
                    zone: locationZone || '--',
                    tiv: totalInsurableValue,
                    dedBBPremium: -1,
                    baseRate,
                    CR_DB,
                    finalRate,
                    totalPremium,
                    lumenRating,
                };
            }
        );
        setGridData(data);
    };

    const updateDedBuyBackData = (buyBackData: DedBuyBackData) => {
        const found = gridData.find((item) => item.locationId === buyBackData.locationId);
        if (found) {
            found.dedBBPremium = Number(buyBackData.totalPrem);
            setGridData([...gridData]);
            dbbData[buyBackData.locationId] = buyBackData;
        }
        setCurrentDbbItem(undefined);
    };

    const fetchDedBuyBackData = async () => {
        const dbbDataUpdate = await fetchDBBData(selectedUWReview.underwriterReview.id);
        setDbbData(
            dbbDataUpdate.reduce((allDbbData, item) => {
                allDbbData[item.locationId] = item;
                return allDbbData;
            }, {} as Record<string, DedBuyBackData>)
        );
    };

    const onDBBRefresh = useCallback(
        (locationId: string) => {
            setCurrentDbbItem(dbbData[locationId] || { locationId, totalPrem: -1, buyBackDed: 0.01 });
        },
        [dbbData]
    );

    React.useEffect(() => {
        createGridData();
        fetchDedBuyBackData();
    }, [selectedUWReview, locations]);

    React.useEffect(() => {
        Object.values(dbbData).forEach((item) => updateDedBuyBackData(item));
    }, [dbbData]);

    if (isLoading || submissionIsLoading) return <CircularLoader />;

    const primary = selectedUWReview?.underwriterReview?.primaryLimit || '';

    return (
        <>
            <Page title={`Commercial Property Underwriting Summary${primary ? ' (' + primary + ')' : ''}`}>
                <Grid container spacing={3}>
                    <Grid>
                        <LabeledData
                            title={'Agent Commission'}
                            value={
                                formatPercent(selectedUWReview?.underwriterReview?.brokerCommissionPercentage, 1) ||
                                '--'
                            }
                        />
                    </Grid>
                    <Grid>
                        <LabeledData
                            title={'Policy Fee'}
                            value={formatDollar(selectedUWReview?.underwriterReview?.policyFee) || '--'}
                        />
                    </Grid>
                    <Grid>
                        <LabeledData
                            title={'Professional Fee'}
                            value={formatDollar(selectedUWReview?.underwriterReview?.professionalFee) || '--'}
                        />
                    </Grid>
                    <Grid>
                        <LabeledData
                            title={'Inspection Fee'}
                            value={formatDollar(selectedUWReview?.underwriterReview?.inspectionFee) || '--'}
                        />
                    </Grid>
                </Grid>
                <Spacing height={'24px'} />
                <Typography variant={'h5'}>Location Reviews</Typography>
                <LrGrid
                    defaultColDef={defaultColDef}
                    columnDefs={columnDefs(onDBBRefresh)}
                    rowData={gridData}
                    height={'25vh'}
                    // domLayout={'autoHeight'}
                    sizeToFit
                />
            </Page>
            {currentDbbItem && (
                <DBBCreateQuoteDialog
                    dbbDataItem={currentDbbItem}
                    onQuoteCreate={updateDedBuyBackData}
                    onCancel={() => setCurrentDbbItem(undefined)}
                    uwReviewId={selectedUWReview.underwriterReview.id}
                />
            )}
        </>
    );
};

export default React.memo(UwSummary);
